import type {
  AuthUser,
  ChangePasswordInput,
  LoginInput,
  PasswordLinkInfo,
  SetPasswordInput,
  UpdatePreferencesInput,
  UpdateProfileForm,
} from '@hemmafixa/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { ApiError } from '@/lib/api';
import { api } from '@/lib/api-client';

export const sessionKey = ['auth', 'me'] as const;

/** The signed-in user, or null when logged out. Every protected page reads this. */
export function useSession() {
  return useQuery({
    queryKey: sessionKey,
    queryFn: async () => {
      try {
        return await api<AuthUser>('/auth/me');
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 5 * 60_000,
  });
}

// Pages inside an area read the user with useCurrentUser (./current-user), which RequireArea provides
export { useCurrentUser } from './current-user';

/** Ends this device's session, goes to the login page and forgets everything cached for the user. */
export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: () => api<void>('/auth/logout', { method: 'POST' }),
    onSuccess: async () => {
      // Leave the area first: clearing the session while it is open would send the next login back to this page
      await navigate('/login', { replace: true });
      queryClient.clear();
      queryClient.setQueryData(sessionKey, null);
    },
  });
}

/** Stores the user a login or password form returned, so protected pages open without another request. */
function useSignedIn() {
  const queryClient = useQueryClient();
  return ({ user }: { user: AuthUser }) => {
    queryClient.setQueryData(sessionKey, user);
  };
}

export function useLogin() {
  const onSuccess = useSignedIn();
  return useMutation({
    mutationFn: (input: LoginInput) =>
      api<{ user: AuthUser }>('/auth/login', { method: 'POST', body: input }),
    onSuccess,
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => api('/auth/password/forgot', { method: 'POST', body: { email } }),
  });
}

/** Whether a set-password or reset link still works. Null when it does not. */
export function usePasswordLink(token: string | null) {
  return useQuery({
    queryKey: ['auth', 'password-link', token],
    enabled: token !== null,
    queryFn: async () => {
      try {
        return await api<PasswordLinkInfo>('/auth/password/link', { method: 'POST', body: { token } });
      } catch (error) {
        if (error instanceof ApiError && (error.status === 404 || error.status === 400)) return null;
        throw error;
      }
    },
    staleTime: Infinity,
  });
}

export function useSetPassword() {
  const onSuccess = useSignedIn();
  return useMutation({
    mutationFn: (input: SetPasswordInput) =>
      api<{ user: AuthUser }>('/auth/password/set', { method: 'POST', body: input }),
    onSuccess,
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProfileForm) => api<AuthUser>('/auth/me', { method: 'PATCH', body: input }),
    onSuccess: (user) => queryClient.setQueryData(sessionKey, user),
  });
}

/**
 * Saves language and sidebar state on the account. One request at a time (`scope`), so quick changes reach the
 * server in order and an older answer never overwrites a newer choice.
 */
export function useUpdatePreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    scope: { id: 'account-preferences' },
    mutationFn: (input: UpdatePreferencesInput) =>
      api<AuthUser>('/auth/me/preferences', { method: 'PATCH', body: input }),
    onSuccess: (user) => queryClient.setQueryData(sessionKey, user),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: ChangePasswordInput) =>
      api<void>('/auth/password/change', { method: 'POST', body: input }),
  });
}
